import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Breadcrumbs,
  ConfirmDialog,
} from '@travellegacy/nexops-design-system';
import {
  ArrowLeft,
  RefreshCw,
  User as UserIcon,
  Mail,
  Phone,
  Hash,
  Calendar,
  Sparkles,
  AlertTriangle,
  ExternalLink,
  Info,
  Send,
  Link as LinkIcon,
  Copy,
  Users,
  CheckCircle2,
} from 'lucide-react';
import { toast } from '@/shared/lib/toast';

import { onboardingCasesAPI } from '@/shared/lib/api';
import {
  STAGE_LABELS,
  STAGE_COLORS,
  EVENT_LABELS,
  OnboardingEventType,
  type OnboardingCaseWithEvents,
  type OnboardingEvent,
  type RetryAiAnalysisResponse,
} from '../types';

/**
 * Página de detalhe de um caso de onboarding.
 *
 * Rota: `/onboarding/:id`.
 *
 * Mostra:
 *  - Dados do cliente
 *  - Etapa atual + timeline de eventos
 *  - Resumo do form_1 e análise IA
 *  - Botão para reprocessar IA (com opção `force` se já houver análise)
 */
interface SendCallLinkResponse {
  summary: string;
  caseId: string;
  stageBefore?: string;
  stageAfter?: string;
  schedulingLink?: string | null;
  callSchedulingToken?: string;
  clientMessageSent?: boolean;
  teamNotified?: boolean;
  skipped?: boolean;
  reason?: string;
  currentStage?: string;
}

export function ClientOnboardingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  const [showSendCallConfirm, setShowSendCallConfirm] = useState(false);
  const [lastSchedulingLink, setLastSchedulingLink] = useState<string | null>(null);

  const { data: caseData, isLoading } = useQuery({
    queryKey: ['onboarding-cases', 'detail', id],
    queryFn: async () => {
      const response = await onboardingCasesAPI.getById(id!);
      return response.data.data as OnboardingCaseWithEvents;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
  });

  const retryMutation = useMutation({
    mutationFn: async (force: boolean) => {
      const response = await onboardingCasesAPI.retryAiAnalysis(id!, force);
      return response.data.data as RetryAiAnalysisResponse;
    },
    onSuccess: (result) => {
      if (result.ok) {
        toast.success('Análise de IA reprocessada com sucesso');
      } else {
        toast.error(mapRetryReason(result.reason) ?? result.error ?? 'Não foi possível reprocessar');
      }
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases', 'list'] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Erro ao reprocessar IA';
      toast.error(message);
    },
  });

  const sendCallLinkMutation = useMutation({
    mutationFn: async () => {
      const response = await onboardingCasesAPI.sendCallLink(id!);
      return response.data.data as SendCallLinkResponse;
    },
    onSuccess: (result) => {
      if (result.skipped) {
        toast.error(
          result.reason === 'invalid_stage'
            ? `Este caso não está aguardando envio de link (stage: ${result.currentStage}).`
            : (result.summary ?? 'Nada a fazer')
        );
      } else {
        if (result.schedulingLink) {
          setLastSchedulingLink(result.schedulingLink);
        }
        if (result.clientMessageSent) {
          toast.success('Link de agendamento enviado ao cliente ✅');
        } else {
          toast.success('Link de agendamento gerado');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases', 'list'] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Erro ao enviar link de agendamento';
      toast.error(message);
    },
  });

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-4 w-64 bg-muted rounded" />
        <div className="h-8 w-96 bg-muted rounded" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-40 bg-muted rounded" />
          <div className="h-40 bg-muted rounded" />
        </div>
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Not found
  // ------------------------------------------------------------------

  if (!caseData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/onboarding">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Caso não encontrado</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Este caso de onboarding não existe ou foi removido.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const notionUrl = `https://www.notion.so/${caseData.notionPageId.replace(/-/g, '')}`;

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Onboarding', href: '/onboarding' },
          { label: caseData.clientName },
        ]}
      />

      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/onboarding">
          <Button variant="ghost" size="icon" className="mt-1">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge variant="outline" className={STAGE_COLORS[caseData.currentStage]}>
              {STAGE_LABELS[caseData.currentStage]}
            </Badge>
            {caseData.dashboardUserCreated && (
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                Usuário criado
              </Badge>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground break-words">
            {caseData.clientName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono break-all">
            ID: {caseData.id}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={notionUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-1" />
              Notion
            </Button>
          </a>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (caseData.aiAnalysis) {
                setShowRetryConfirm(true);
              } else {
                retryMutation.mutate(false);
              }
            }}
            disabled={retryMutation.isPending}
          >
            <RefreshCw
              className={`h-4 w-4 mr-1 ${retryMutation.isPending ? 'animate-spin' : ''}`}
            />
            Reprocessar IA
          </Button>
          {caseData.currentStage === 'AWAITING_CALL_SCHEDULE_TRIGGER' && (
            <Button
              size="sm"
              onClick={() => setShowSendCallConfirm(true)}
              disabled={sendCallLinkMutation.isPending}
            >
              <Send
                className={`h-4 w-4 mr-1 ${sendCallLinkMutation.isPending ? 'animate-pulse' : ''}`}
              />
              Enviar link de agendamento
            </Button>
          )}
        </div>
      </div>

      {/* Banner: Onboarding concluído */}
      {caseData.currentStage === 'COMPLETED' && (
        <Card className="border-green-300 bg-green-50">
          <CardContent className="py-4 flex items-start gap-3 flex-wrap">
            <CheckCircle2 className="h-6 w-6 text-green-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-green-900">Onboarding concluído</p>
              <p className="text-sm text-green-800 mt-0.5">
                O cliente agendou a call e o grupo WhatsApp foi criado.
                {caseData.completedAt && (
                  <> Finalizado em {formatDate(caseData.completedAt)}.</>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card: Grupo WhatsApp do cliente */}
      {(caseData.whatsappGroupId || caseData.whatsappGroupInvite) && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="py-4 space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-700" />
              <p className="font-medium text-emerald-900">Grupo WhatsApp do cliente</p>
            </div>

            {caseData.whatsappGroupId && (
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-emerald-700 uppercase tracking-wide">Group ID</p>
                  <p className="text-xs text-emerald-900 mt-0.5 font-mono break-all">
                    {caseData.whatsappGroupId}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyLink(caseData.whatsappGroupId!)}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copiar
                </Button>
              </div>
            )}

            {caseData.whatsappGroupInvite && (
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-emerald-700 uppercase tracking-wide">
                    Link de convite
                  </p>
                  <p className="text-xs text-emerald-900 mt-0.5 font-mono break-all">
                    {caseData.whatsappGroupInvite}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyLink(caseData.whatsappGroupInvite!)}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copiar
                  </Button>
                  <a
                    href={caseData.whatsappGroupInvite}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Abrir
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Link de agendamento (após envio bem-sucedido) */}
      {lastSchedulingLink && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="py-4 flex items-start gap-3 flex-wrap">
            <LinkIcon className="h-5 w-5 text-emerald-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-emerald-900">Link de agendamento</p>
              <p className="text-xs text-emerald-800 mt-0.5 font-mono break-all">
                {lastSchedulingLink}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopyLink(lastSchedulingLink)}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copiar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Confirmação retry (inline card) */}
      {showRetryConfirm && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Já existe uma análise de IA para este caso.</p>
                <p className="text-sm text-amber-800">
                  Reprocessar irá sobrescrever a análise atual. Deseja continuar?
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowRetryConfirm(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setShowRetryConfirm(false);
                  retryMutation.mutate(true);
                }}
                disabled={retryMutation.isPending}
              >
                Sobrescrever
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmação: enviar link de agendamento */}
      <ConfirmDialog
        open={showSendCallConfirm}
        onOpenChange={setShowSendCallConfirm}
        title="Enviar link de agendamento"
        icon="execute"
        variant="default"
        confirmText="Enviar link"
        cancelText="Cancelar"
        isLoading={sendCallLinkMutation.isPending}
        onConfirm={() => sendCallLinkMutation.mutate()}
        description={
          <div className="space-y-3">
            <p>
              Você está prestes a enviar o link de agendamento de call para{' '}
              <strong>{caseData.clientName}</strong>
              {caseData.clientPhone ? (
                <>
                  {' '}
                  no WhatsApp{' '}
                  <span className="font-mono text-xs">{caseData.clientPhone}</span>
                </>
              ) : null}
              .
            </p>

            <div className="rounded-md border border-border bg-muted/40 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ao confirmar, o sistema vai:
              </p>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                <li>Gerar um token e um link único de agendamento para o cliente</li>
                <li>Enviar a mensagem com o link no WhatsApp do cliente</li>
                <li>Notificar o time interno no grupo do operacional</li>
                <li>
                  Avançar o case para{' '}
                  <span className="font-medium">"Link de call enviado"</span>
                </li>
              </ol>
            </div>

            {!caseData.clientPhone && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  Este caso não possui telefone cadastrado — o envio via
                  WhatsApp vai falhar.
                </span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Depois disso, o cliente será quem escolhe o horário. Assim que ele
              agendar, a Etapa 04 é disparada automaticamente (criação de grupo,
              capa, descrição, boas-vindas).
            </p>
          </div>
        }
      />

      {/* Grid: Dados do cliente + Datas */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              Dados do cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Field label="Nome" value={caseData.clientName} icon={<UserIcon className="h-4 w-4" />} />
            <Field label="E-mail" value={caseData.clientEmail} icon={<Mail className="h-4 w-4" />} />
            <Field label="Telefone" value={caseData.clientPhone} icon={<Phone className="h-4 w-4" />} />
            <Field
              label="CPF"
              value={caseData.clientCpf}
              icon={<Hash className="h-4 w-4" />}
              mono
            />
            <Field
              label="Data de nascimento"
              value={caseData.clientBirthDate ? formatDate(caseData.clientBirthDate, false) : null}
              icon={<Calendar className="h-4 w-4" />}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Linha do tempo (marcos)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Field label="Criado em" value={formatDate(caseData.createdAt)} />
            <Field label="Atualizado em" value={formatDate(caseData.updatedAt)} />
            <Field label="Form 1 recebido" value={formatDate(caseData.form1CompletedAt)} />
            <Field label="Form 2 recebido" value={formatDate(caseData.form2CompletedAt)} />
            <Field label="Link de call enviado" value={formatDate(caseData.callLinkSentAt)} />
            <Field label="Call agendada" value={formatDate(caseData.callScheduledAt)} />
            <Field label="Onboarding concluído" value={formatDate(caseData.completedAt)} />
            {caseData.failedAt && (
              <>
                <Field label="Falhou em" value={formatDate(caseData.failedAt)} />
                <Field label="Motivo" value={caseData.failureReason} />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Análise de IA
            {caseData.aiAnalysisAt && (
              <span className="text-xs font-normal text-muted-foreground">
                (atualizada em {formatDate(caseData.aiAnalysisAt)})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {caseData.aiAnalysisError && !caseData.aiAnalysis && (
            <div className="flex items-start gap-2 rounded border border-red-300 bg-red-50 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Falha na última tentativa</p>
                <p className="text-red-800">{caseData.aiAnalysisError}</p>
              </div>
            </div>
          )}
          {caseData.aiAnalysis ? (
            <pre className="whitespace-pre-wrap break-words text-sm bg-muted p-4 rounded max-w-full">
              {caseData.aiAnalysis}
            </pre>
          ) : (
            !caseData.aiAnalysisError && (
              <p className="text-sm text-muted-foreground">
                Ainda sem análise de IA disponível.
              </p>
            )
          )}
        </CardContent>
      </Card>

      {/* Form 1 summary */}
      {caseData.form1Summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="h-5 w-5" />
              Resumo do Form 1
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap break-words text-sm bg-muted p-4 rounded max-w-full">
              {caseData.form1Summary}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Timeline de eventos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Eventos ({caseData.events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {caseData.events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum evento registrado ainda.
            </p>
          ) : (
            <ol className="space-y-2">
              {caseData.events.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------
// Subcomponentes
// ------------------------------------------------------------------

interface FieldProps {
  label: string;
  value: string | null | undefined;
  icon?: React.ReactNode;
  mono?: boolean;
}

function Field({ label, value, icon, mono }: FieldProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex items-center gap-1.5 text-muted-foreground shrink-0">
        {icon}
        {label}
      </span>
      <span
        className={`text-right text-foreground break-words ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value ?? <span className="text-muted-foreground italic">—</span>}
      </span>
    </div>
  );
}

function EventRow({ event }: { event: OnboardingEvent }) {
  const isError =
    event.type === OnboardingEventType.ERROR ||
    event.type === OnboardingEventType.AI_ANALYSIS_FAILED;
  const isWarn = event.type === OnboardingEventType.WARNING;
  const rowClass = isError
    ? 'border-red-200 bg-red-50'
    : isWarn
      ? 'border-amber-200 bg-amber-50'
      : 'border-border bg-card';

  return (
    <li className={`rounded border p-3 ${rowClass}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {EVENT_LABELS[event.type] ?? event.type}
          </Badge>
          {event.stageBefore && event.stageAfter && (
            <span className="text-xs text-muted-foreground">
              {STAGE_LABELS[event.stageBefore]} → {STAGE_LABELS[event.stageAfter]}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatDate(event.createdAt)}
        </span>
      </div>
      <p className="text-sm mt-1 text-foreground break-words">{event.message}</p>
      {event.actorType && (
        <p className="text-xs text-muted-foreground mt-1">
          Origem: {event.actorType}
          {event.actorId ? ` (${event.actorId})` : ''}
        </p>
      )}
    </li>
  );
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function formatDate(iso: string | null | undefined, withTime = true): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  } catch {
    return iso;
  }
}

function mapRetryReason(reason: RetryAiAnalysisResponse['reason']): string | null {
  switch (reason) {
    case 'already_has_analysis':
      return 'Já existe análise de IA. Use "sobrescrever" para forçar.';
    case 'no_form_summary':
      return 'Não há resumo do Form 1 para analisar.';
    case 'ai_not_configured':
      return 'A integração de IA não está configurada.';
    default:
      return null;
  }
}
